import {
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'src/components/snackbar';
import { formApi, GetFormDto } from 'src/api/form';
import { translateKey } from 'src/locales/translate';


type Props = {
  serviceId: string;
  organizationId: string;
};

export default function ServiceFormAssociation({
  serviceId,
  organizationId,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Fetch all forms for this organization
  const { data: formsResponse, isLoading: isFormsLoading } = useQuery(
    ['forms', organizationId],
    () => formApi.getFormsByOrganization(organizationId),
    { enabled: !!organizationId },
  );

  // Fetch current form associations for this service
  const { data: serviceFormsResponse, isLoading: isServiceFormsLoading } =
    useQuery(
      ['service-forms', serviceId],
      () => formApi.getServiceForms(serviceId),
      { enabled: !!serviceId },
    );

  const allForms = formsResponse?.data || [];
  const currentFormIds: string[] =
    (serviceFormsResponse?.data as any)?.formIds ||
    serviceFormsResponse?.data ||
    [];

  // Map current form IDs to form objects
  const selectedForms = allForms.filter((f) =>
    currentFormIds.includes(f.id),
  );

  const updateMutation = useMutation(
    (formIds: string[]) => formApi.updateServiceForms(serviceId, formIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-forms', serviceId]);
        enqueueSnackbar(translateKey("hardcoded.formularze_zostaly_zaktualizowane"), {
          variant: 'success',
        });
      },
      onError: () => {
        enqueueSnackbar(translateKey("hardcoded.blad_podczas_aktualizacji_formularzy"), {
          variant: 'error',
        });
      },
    },
  );

  const handleChange = (_: any, newValue: GetFormDto[]) => {
    updateMutation.mutate(newValue.map((f) => f.id));
  };

  if (isFormsLoading || isServiceFormsLoading) {
    return <CircularProgress size={16} />;
  }

  if (allForms.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Wymagane formularze:
      </Typography>
      <Autocomplete
        multiple
        size="small"
        options={allForms}
        value={selectedForms}
        onChange={handleChange}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={updateMutation.isLoading}
        renderTags={(value, getTagProps) =>
          value.map((form, index) => (
            <Chip
              label={form.name}
              size="small"
              variant="outlined"
              color="primary"
              {...getTagProps({ index })}
              key={form.id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selectedForms.length === 0 ? 'Dodaj formularz...' : ''
            }
            size="small"
            sx={{ '& .MuiInputBase-root': { py: 0.5 } }}
          />
        )}
        noOptionsText={translateKey("hardcoded.brak_formularzy_utworz_je_w_zakladce_formularze")}
      />
    </Stack>
  );
}
